import { Cart, nativeAppHeaders, ResponseError } from '@ynap/api-utils'
import { getLocale } from '@ynap/cart-state'
import { getTimeout, getWatsonConfig } from '@ynap/cart-state/src/selectors/getConfig'
import { raygun } from '@ynap/clientside-monitoring'
import { collectBrowserInfo } from '../../../adyen'
import { FAST_CHECKOUT_ERROR, FAST_CHECKOUT_REQUEST, FAST_CHECKOUT_SUCCESS } from '../constants/ActionTypes'
import { getCurrentBillingAddress, getCurrentPaymentOption } from '../selectors/getPaymentStep'
import { isShippingAddressValid } from '../utils/addressValidationUtils'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { getCardholderName } from '../utils/paymentUtils'
import { checkRectificationOnSuccess } from './cartRectification'
import wrapFetchWithJSON from './common/wrapFetchWithJSON'

import type { Dispatch } from 'redux'
import type { StoreState } from 'app/state/types'
import type { ShoppingCart } from 'app/types/common-types'

type CartResponseData = {
    shoppingCart: ShoppingCart
}

type FastCheckoutValues = {
    returnUrl: string
    isApplePay: boolean
    applePayValidationUrl: string
}

function fastCheckoutRequest() {
    return {
        type: FAST_CHECKOUT_REQUEST,
    }
}

function fastCheckoutSuccess(payload) {
    return {
        type: FAST_CHECKOUT_SUCCESS,
        payload,
    }
}

function fastCheckoutError(error) {
    return {
        type: FAST_CHECKOUT_ERROR,
        error,
    }
}

export default function fastCheckout(values: FastCheckoutValues) {
    return async (dispatch: Dispatch, getState: () => StoreState, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))
        const watsonConfig = getWatsonConfig(state)
        const { langId, id } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }
        dispatch(fastCheckoutRequest())

        const customRedirect = () => new ResponseError({ status: 401 })
        try {
            let json: CartResponseData = await fetchJSON(Cart.applyCheckoutProfile({ ...watsonConfig, langId, additionalHeaders }), { customRedirect })

            const nextState = { ...state, cart: json.shoppingCart }
            const currentPaymentOption = getCurrentPaymentOption(nextState)
            const currentBillingAddress = getCurrentBillingAddress(nextState)
            const buyerId = json?.shoppingCart?.x_buyerId
            const { returnUrl, isApplePay = false, applePayValidationUrl } = values

            if (buyerId) {
                raygun.helpers.agent('setUser', { identifier: buyerId })
            }
            await isShippingAddressValid({ ...state, cart: json.shoppingCart }, dispatch)
            // We need to resend payment instruction to set browserInfo
            if (currentPaymentOption && currentPaymentOption.cardId && currentPaymentOption.saved && !isApplePay) {
                const browserInfo = collectBrowserInfo()

                const ccExpiryMonth = currentPaymentOption.expireMonth!
                const ccExpiryYear = currentPaymentOption.expireYear!

                json = await fetchJSON(
                    Cart.setPaymentInstruction({
                        paymentInstruction: [
                            {
                                payMethodId: 'CREDIT_CARD',
                                billing_address_id: currentBillingAddress?.addressId,
                                apiToken: currentPaymentOption.cardId,
                                cc_type: currentPaymentOption.type,
                                cc_expiryMonth: ccExpiryMonth.length < 2 ? `0${ccExpiryMonth}` : ccExpiryMonth,
                                cc_expiryYear: ccExpiryYear.length <= 2 ? `20${ccExpiryYear}` : ccExpiryYear,
                                cc_lastFourDigits: currentPaymentOption.lastFourDigits,
                                cc_cvv_entered: currentPaymentOption.saved ? undefined : 'true',
                                cc_cardHolderName: getCardholderName(currentPaymentOption, currentBillingAddress),
                                returnUrl,
                                ...browserInfo,
                            },
                        ],
                        ...watsonConfig,
                        langId,
                        additionalHeaders,
                    })
                )
            }
            if ((currentPaymentOption && currentPaymentOption.id === 'APPLEPAY') || isApplePay) {
                json = await fetchJSON(
                    Cart.setPaymentInstruction({
                        paymentInstruction: [
                            {
                                'params.1.name': 'validationUrl',
                                'params.1.value': applePayValidationUrl,
                                payMethodId: 'APPLEPAY',
                                billing_address_id: currentBillingAddress?.addressId,
                                returnUrl,
                                cancelUrl: returnUrl,
                            },
                        ],
                        ...watsonConfig,
                        langId,
                        additionalHeaders,
                    })
                )
            }
            dispatch(fastCheckoutSuccess(json))
            checkRectificationOnSuccess(dispatch, json)
        } catch (error: any) {
            try {
                if (error.response) {
                    await error.response.json().then(({ errors }) => {
                        if (errors.find(({ errorKey }) => errorKey === 'ERR_CART_IS_EMPTY')) {
                            return window.location.assign(`/${id}/checkout`)
                        } else if (errors.find(({ errorKey }) => errorKey === 'APPLY_FASTCHK_PAYMENT_FAILED')) {
                            throw new Error('APPLY_FASTCHK_PAYMENT_FAILED')
                        } else {
                            throw error
                        }
                    })
                } else {
                    throw error
                }
            } catch (otherError) {
                raygun.helpers.agent('send', { error: otherError, tags: ['fastCheckout'] })
                dispatch(fastCheckoutError(otherError))
                throw otherError
            }
        }
    }
}
